import { useState, type FormEvent, type ReactElement } from 'react';
import { Link, useNavigate } from 'react-router';
import { messageForError } from '@proctoring/api-client';
import { createInterviewRequestSchema, type CreateInterviewRequest } from '@proctoring/shared';
import { useCreateInterview, useInterviewers, useJobProfiles } from '../api/queries.js';
import { FullPageMessage } from '../components/FullPageMessage.js';

interface DraftState {
  candidateEmail: string;
  candidateFullName: string;
  jobProfileId: string;
  interviewerIds: string[];
  /** `datetime-local` value: wall-clock, no zone — converted on submit. */
  scheduledAtLocal: string;
}

const EMPTY: DraftState = {
  candidateEmail: '',
  candidateFullName: '',
  jobProfileId: '',
  interviewerIds: [],
  scheduledAtLocal: '',
};

/** Field-name → label, so a schema issue can be reported against the input the admin sees. */
const FIELD_LABELS: Record<string, string> = {
  candidateEmail: 'Candidate email',
  candidateFullName: 'Candidate full name',
  jobProfileId: 'Job profile',
  interviewerIds: 'Interviewers',
  scheduledAt: 'Scheduled time',
};

export interface BuildResult {
  ok: boolean;
  payload?: CreateInterviewRequest;
  message?: string;
}

/**
 * Converts the form draft into the API payload and validates it against the shared schema.
 *
 * Exported so the shape the form submits is testable without driving the DOM. The
 * `datetime-local` input yields a zone-less wall-clock string in the admin's own timezone;
 * `new Date(...)` interprets it locally and `toISOString()` normalises it to the UTC
 * instant the API stores — the admin picks "2pm my time" and that is what is scheduled.
 */
export function buildCreateInterviewPayload(draft: DraftState): BuildResult {
  if (!draft.scheduledAtLocal) {
    return { ok: false, message: 'Scheduled time: pick a date and time.' };
  }
  const scheduled = new Date(draft.scheduledAtLocal);
  if (Number.isNaN(scheduled.getTime())) {
    return { ok: false, message: 'Scheduled time: not a valid date.' };
  }

  const candidate = {
    candidateEmail: draft.candidateEmail.trim(),
    candidateFullName: draft.candidateFullName.trim(),
    jobProfileId: draft.jobProfileId,
    interviewerIds: draft.interviewerIds,
    scheduledAt: scheduled.toISOString(),
  };

  const parsed = createInterviewRequestSchema.safeParse(candidate);
  if (!parsed.success) {
    const issue = parsed.error.issues[0];
    const label = FIELD_LABELS[String(issue?.path[0] ?? '')] ?? 'Form';
    return { ok: false, message: `${label}: ${issue?.message ?? 'is invalid'}` };
  }
  return { ok: true, payload: parsed.data };
}

export function CreateInterviewPage(): ReactElement {
  const navigate = useNavigate();
  const profiles = useJobProfiles();
  const interviewers = useInterviewers();
  const create = useCreateInterview();

  const [draft, setDraft] = useState<DraftState>(EMPTY);
  const [error, setError] = useState<string | null>(null);

  if (profiles.isPending || interviewers.isPending) {
    return <FullPageMessage title="Loading form…" />;
  }
  if (profiles.error || interviewers.error) {
    return (
      <FullPageMessage
        title="Could not load the form"
        detail={messageForError(profiles.error ?? interviewers.error)}
      />
    );
  }

  function toggleInterviewer(id: string): void {
    setDraft((current) => ({
      ...current,
      interviewerIds: current.interviewerIds.includes(id)
        ? current.interviewerIds.filter((existing) => existing !== id)
        : [...current.interviewerIds, id],
    }));
  }

  async function onSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    const result = buildCreateInterviewPayload(draft);
    if (!result.ok || !result.payload) {
      setError(result.message ?? 'The form is incomplete.');
      return;
    }
    setError(null);
    try {
      const interview = await create.mutateAsync(result.payload);
      navigate(`/interviews/${interview.id}`, { replace: true });
    } catch (cause) {
      setError(messageForError(cause));
    }
  }

  return (
    <div className="mx-auto w-full max-w-2xl px-6 py-10">
      <h1 className="text-xl font-semibold tracking-tight text-session-text">New interview</h1>
      <p className="mt-1 text-sm text-session-muted">
        Creating an interview provisions the room and emails invitations to the candidate and every
        interviewer.
      </p>

      <form onSubmit={onSubmit} className="panel mt-8 space-y-5 p-5" noValidate>
        {error ? (
          <p role="alert" className="form-error">
            {error}
          </p>
        ) : null}

        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label htmlFor="candidateFullName" className="field-label">
              Candidate full name
            </label>
            <input
              id="candidateFullName"
              value={draft.candidateFullName}
              onChange={(event) => setDraft({ ...draft, candidateFullName: event.target.value })}
              className="field-input"
            />
          </div>
          <div>
            <label htmlFor="candidateEmail" className="field-label">
              Candidate email
            </label>
            <input
              id="candidateEmail"
              type="email"
              value={draft.candidateEmail}
              onChange={(event) => setDraft({ ...draft, candidateEmail: event.target.value })}
              className="field-input"
            />
          </div>
        </div>

        <div>
          <label htmlFor="jobProfileId" className="field-label">
            Job profile
          </label>
          <select
            id="jobProfileId"
            value={draft.jobProfileId}
            onChange={(event) => setDraft({ ...draft, jobProfileId: event.target.value })}
            className="field-input"
          >
            <option value="">Select a job profile…</option>
            {profiles.data.map((profile) => (
              <option key={profile.id} value={profile.id}>
                {profile.name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="scheduledAt" className="field-label">
            Scheduled time (your local time)
          </label>
          <input
            id="scheduledAt"
            type="datetime-local"
            value={draft.scheduledAtLocal}
            onChange={(event) => setDraft({ ...draft, scheduledAtLocal: event.target.value })}
            className="field-input"
          />
        </div>

        <fieldset>
          <legend className="field-label">Interviewers (1–4)</legend>
          {interviewers.data.length === 0 ? (
            <p className="text-sm text-session-muted">
              No interviewer accounts exist yet.{' '}
              <Link to="/interviewers" className="text-session-accent hover:underline">
                Invite one
              </Link>{' '}
              before scheduling an interview.
            </p>
          ) : (
            <div className="mt-1 space-y-2">
              {interviewers.data.map((person) => (
                <label
                  key={person.id}
                  className="flex items-center gap-3 rounded-md border border-session-border bg-session-panel-raised px-3 py-2"
                >
                  <input
                    type="checkbox"
                    checked={draft.interviewerIds.includes(person.id)}
                    onChange={() => toggleInterviewer(person.id)}
                    className="accent-session-accent"
                  />
                  <span className="text-sm text-session-text">{person.fullName}</span>
                  <span className="text-xs text-session-dim">{person.email}</span>
                </label>
              ))}
            </div>
          )}
        </fieldset>

        <div className="flex gap-3">
          <button type="submit" disabled={create.isPending} className="btn-primary">
            {create.isPending ? 'Creating…' : 'Create interview'}
          </button>
          <button type="button" onClick={() => navigate('/interviews')} className="btn-secondary">
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}
